import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import {
  Car, MapPin, Users, Star, ShieldCheck, Loader2, Navigation, Send, MessageCircle,
  Bell, CheckCircle2, Clock, X, Sparkles, Search, BadgeCheck, Coins, ArrowRight,
  Trash2, Play, RefreshCw, Flag, Route, Heart, Leaf, Globe, HandHeart, UsersRound,
  Home, BedDouble, Utensils, Plus, CalendarDays, ThumbsUp, Zap, Repeat,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Price } from "@/components/Price";
import { FileUpload } from "@/components/FileUpload";
import { AMENITIES, ACCOM_LABEL, FOOD_LABEL, STAY_STATUS, isUpcomingStay } from "@/lib/stays";
import { toast } from "sonner";

const STATUS_STYLE = {
  pending: { c: "#f59e0b", label: "Awaiting confirmation" },
  confirmed: { c: "#22c55e", label: "Confirmed" },
  in_progress: { c: "#3b82f6", label: "On the way" },
  completed: { c: "#84cc16", label: "Completed" },
  cancelled: { c: "#ef4444", label: "Cancelled" },
};

function Stars({ score = 0, count = 0, size = 13 }) {
  const s = Number(score) || 0;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-sec" title={`${s}★ from ${count} reviews`}>
      <Star size={size} className="text-brand" fill="currentColor" /> {s ? s.toFixed(1) : "New"}{count ? ` · ${count}` : ""}
    </span>
  );
}

function VerifiedPill({ verified }) {
  return verified ? (
    <span className="inline-flex items-center gap-1 text-[11px] text-brand"><BadgeCheck size={13} /> Verified</span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[11px] text-sec"><ShieldCheck size={13} /> Unverified</span>
  );
}

function AddressInput({ value, onSelect, placeholder, testId }) {
  const [q, setQ] = useState(value?.label || "");
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const tRef = useRef();
  const run = (val) => {
    setQ(val);
    onSelect(null);
    clearTimeout(tRef.current);
    if (val.trim().length < 3) { setOpts([]); return; }
    setBusy(true);
    tRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=6&q=${encodeURIComponent(val)}`, { headers: { "Accept-Language": "en" } });
        const data = await r.json();
        setOpts(data.map((d) => ({ label: d.display_name, lat: parseFloat(d.lat), lng: parseFloat(d.lon), country: (d.address?.country_code || "").toUpperCase() })));
        setOpen(true);
      } catch { setOpts([]); } finally { setBusy(false); }
    }, 400);
  };
  const choose = (o) => { setQ(o.label); onSelect(o); setOpen(false); };
  return (
    <div className="relative">
      <div className="relative">
        <input data-testid={testId} value={q} onChange={(e) => run(e.target.value)} placeholder={placeholder}
          className="rud-input w-full rounded-lg pl-8 pr-3 py-2.5 text-sm" autoComplete="off" />
        <MapPin size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sec" />
        {busy && <Loader2 size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sec animate-spin" />}
      </div>
      {open && opts.length > 0 && (
        <div className="absolute z-40 mt-1 w-full rud-card rounded-lg max-h-56 overflow-auto shadow-xl border border-rud">
          {opts.map((o, i) => (
            <button key={i} type="button" onClick={() => choose(o)} data-testid={`${testId}-opt-${i}`}
              className="block w-full text-left px-3 py-2 text-xs rud-hl truncate">{o.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function RideForm({ role, meta, country, onPosted, verified }) {
  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [seats, setSeats] = useState(role === "driver" ? 3 : 1);
  const [carType, setCarType] = useState("sedan");
  const [radius, setRadius] = useState(meta?.radius_km_default || 15);
  const [notes, setNotes] = useState("");
  const [recurrence, setRecurrence] = useState("none");   // none | daily | weekly
  const [daysOfWeek, setDaysOfWeek] = useState([]);        // 0=Mon .. 6=Sun
  const [endDate, setEndDate] = useState("");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);
  const DOW = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];
  const toggleDow = (d) => setDaysOfWeek((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]));

  useEffect(() => {
    if (!origin || !destination) { setQuote(null); return; }
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.post("/transport/quote", { origin, destination, country: origin?.country || destination?.country || country, car_type: carType, seats: 1 });
        if (!cancel) setQuote(data);
      } catch { if (!cancel) setQuote(null); }
    })();
    return () => { cancel = true; };
  }, [origin, destination, carType, country]);

  const submit = async () => {
    if (!origin || !destination) { toast.error("Pick a departure and destination from the suggestions."); return; }
    if (!date) { toast.error("Choose a travel date."); return; }
    if (recurrence !== "none" && !endDate) { toast.error("Choose an end date for your recurring trip."); return; }
    if (recurrence === "weekly" && daysOfWeek.length === 0) { toast.error("Pick at least one day of the week."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/transport/rides", {
        role, origin, destination, date, time, seats: Number(seats), car_type: carType,
        country: origin?.country || destination?.country || country,
        notes, radius_km: Number(radius),
        recurrence, days_of_week: daysOfWeek, end_date: endDate,
      });
      const many = data.occurrences > 1 ? ` across ${data.occurrences} dates` : "";
      toast.success(role === "driver"
        ? `Ride offered${many}! ${data.matches_notified} traveller(s) on your path were alerted.`
        : `Request posted${many}! ${data.matches_notified} driver(s) on your path were alerted.`);
      setOrigin(null); setDestination(null); setDate(""); setTime(""); setNotes(""); setQuote(null);
      setRecurrence("none"); setDaysOfWeek([]); setEndDate("");
      onPosted?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rud-card rounded-2xl p-5 space-y-3" data-testid={`ride-form-${role}`}>
      {role === "driver" && !verified && (
        <div className="flex items-center gap-2 rounded-lg bg-brand/10 border border-brand/30 p-3 text-xs" data-testid="offer-verify-note">
          <ShieldCheck size={15} className="text-brand shrink-0" /> Complete My Kind identity verification (below) before you can offer a ride.
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="text-[11px] uppercase tracking-widest text-sec">From</label>
          <AddressInput testId={`${role}-origin`} value={origin} onSelect={setOrigin} placeholder="Departure city / address" /></div>
        <div><label className="text-[11px] uppercase tracking-widest text-sec">To</label>
          <AddressInput testId={`${role}-destination`} value={destination} onSelect={setDestination} placeholder="Destination city / address" /></div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div><label className="text-[11px] uppercase tracking-widest text-sec">{recurrence === "none" ? "Date" : "Start date"}</label>
          <input type="date" data-testid={`${role}-date`} value={date} onChange={(e) => setDate(e.target.value)} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" /></div>
        <div><label className="text-[11px] uppercase tracking-widest text-sec">Time <span className="opacity-50">(opt)</span></label>
          <input type="time" data-testid={`${role}-time`} value={time} onChange={(e) => setTime(e.target.value)} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" /></div>
        <div><label className="text-[11px] uppercase tracking-widest text-sec">{role === "driver" ? "Seats free" : "Seats"}</label>
          <input type="number" min={1} max={8} data-testid={`${role}-seats`} value={seats} onChange={(e) => setSeats(e.target.value)} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" /></div>
        <div><label className="text-[11px] uppercase tracking-widest text-sec">Match radius (km)</label>
          <input type="number" min={1} max={200} data-testid={`${role}-radius`} value={radius} onChange={(e) => setRadius(e.target.value)} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" /></div>
      </div>

      {/* Recurring travel */}
      <div className="rounded-xl border border-rud bg-elevated/40 p-3 space-y-3" data-testid={`${role}-recurrence`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-widest text-sec flex items-center gap-1.5"><Repeat size={13} className="text-brand" /> Repeat</span>
          {[["none", "One-time"], ["daily", "Daily"], ["weekly", "Days of week"]].map(([val, lbl]) => (
            <button key={val} type="button" data-testid={`${role}-recur-${val}`} onClick={() => setRecurrence(val)}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${recurrence === val ? "bg-brand text-black border-brand" : "bg-elevated/50 text-sec border-rud hover:text-[var(--rud-text)]"}`}>{lbl}</button>
          ))}
        </div>
        {recurrence === "weekly" && (
          <div className="flex flex-wrap gap-1.5" data-testid={`${role}-dow-picker`}>
            {DOW.map(([lbl, d]) => (
              <button key={d} type="button" data-testid={`${role}-dow-${d}`} onClick={() => toggleDow(d)}
                className={`w-11 py-1.5 rounded-lg text-xs border transition-colors ${daysOfWeek.includes(d) ? "bg-brand text-black border-brand" : "bg-elevated/50 text-sec border-rud hover:text-[var(--rud-text)]"}`}>{lbl}</button>
            ))}
          </div>
        )}
        {recurrence !== "none" && (
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-sec">Start on date</label>
              <input type="date" data-testid={`${role}-start-date`} value={date} onChange={(e) => setDate(e.target.value)}
                className="rud-input rounded-lg px-3 py-2.5 text-sm block mt-0.5" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-sec">End by date</label>
              <input type="date" data-testid={`${role}-end-date`} min={date || undefined} value={endDate} onChange={(e) => setEndDate(e.target.value)}
                className="rud-input rounded-lg px-3 py-2.5 text-sm block mt-0.5" />
            </div>
            <p className="text-[11px] text-sec flex-1 min-w-[180px]">
              {recurrence === "daily" ? "We'll post this trip every day" : "We'll post this trip on the selected days each week"} from your start date through the end date.
            </p>
          </div>
        )}
      </div>
      {role === "driver" && (
        <div>
          <label className="text-[11px] uppercase tracking-widest text-sec">Your car</label>
          <div className="flex flex-wrap gap-2 mt-1" data-testid="car-type-picker">
            {(meta?.car_types || []).map((c) => (
              <button key={c.id} type="button" onClick={() => setCarType(c.id)} data-testid={`car-${c.id}`}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${carType === c.id ? "bg-brand text-black border-brand" : "bg-elevated/50 text-sec border-rud hover:text-[var(--rud-text)]"}`}>{c.label}</button>
            ))}
          </div>
        </div>
      )}
      <input data-testid={`${role}-notes`} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (luggage, pets, pickup point, AC…)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />

      {quote && (
        <div className="rounded-xl border border-brand/25 bg-brand/5 p-4" data-testid="fare-quote">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-sm"><Route size={15} className="text-brand" /> ≈ {quote.distance_km} km road distance</div>
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-brand/15 text-brand">~{quote.savings_pct}% below a taxi</span>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3">
            <div><div className="text-[10px] uppercase tracking-widest text-sec">Fare / seat</div><Price usd={quote.per_seat_usd} primaryClass="font-serif text-xl" /></div>
            <div><div className="text-[10px] uppercase tracking-widest text-sec">Driver keeps ({meta?.driver_pct}%)</div><Price usd={quote.driver_earn_usd} primaryClass="font-serif text-xl text-brand" /></div>
            <div><div className="text-[10px] uppercase tracking-widest text-sec">Taxi would cost</div><Price usd={quote.taxi_estimate_usd} primaryClass="font-serif text-xl line-through opacity-60" /></div>
          </div>
          <p className="text-[11px] text-sec mt-2">Cost-share only — {role === "driver" ? "you're travelling anyway; riders chip in for fuel & wear." : "you split the real cost of a trip the driver is already making."} My Kind keeps {meta?.fee_pct}% (Karm Sanya).</p>
        </div>
      )}

      <button data-testid={`${role}-submit`} onClick={submit} disabled={busy || (role === "driver" && !verified)}
        className="btn-primary w-full rounded-full h-11 text-sm flex items-center justify-center gap-2 disabled:opacity-50">
        {busy ? <Loader2 size={16} className="animate-spin" /> : (role === "driver" ? <Car size={16} /> : <Search size={16} />)}
        {role === "driver" ? "Offer this ride" : "Post my request & find drivers"}
      </button>
    </div>
  );
}

function OfferCard({ ride, onBook, canBook }) {
  const [seats, setSeats] = useState(1);
  const [busy, setBusy] = useState(false);
  const book = async () => { setBusy(true); await onBook(ride, Number(seats), () => setBusy(false)); };
  return (
    <div className="rud-card rounded-xl p-4" data-testid={`offer-${ride.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium"><Navigation size={14} className="text-brand shrink-0" />
            <span className="truncate">{ride.origin.label.split(",")[0]} → {ride.destination.label.split(",")[0]}</span></div>
          <div className="text-xs text-sec mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="flex items-center gap-1"><Clock size={12} /> {ride.date}{ride.time ? ` · ${ride.time}` : ""}</span>
            <span className="flex items-center gap-1"><Users size={12} /> {ride.seats_available} left</span>
            <span className="flex items-center gap-1"><Route size={12} /> {ride.distance_km} km</span>
            {ride.match && <span className="text-brand">{ride.match.origin_gap_km}/{ride.match.dest_gap_km} km off</span>}
          </div>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-sm">{ride.user_name}</span>
            <VerifiedPill verified={ride.verified} />
            <Stars score={ride.rep?.score} count={ride.rep?.count} />
          </div>
        </div>
        <div className="text-right">
          <Price usd={ride.per_seat_usd} primaryClass="font-serif text-xl" />
          <div className="text-[10px] text-sec">per seat</div>
        </div>
      </div>
      {ride.notes && <p className="text-xs text-sec mt-2 border-t border-rud pt-2">{ride.notes}</p>}
      {canBook && !ride.is_mine && (
        <div className="flex items-center gap-2 mt-3">
          <input type="number" min={1} max={ride.seats_available} value={seats} onChange={(e) => setSeats(e.target.value)}
            data-testid={`offer-seats-${ride.id}`} className="rud-input w-16 rounded-lg px-2 py-1.5 text-sm" />
          <button data-testid={`book-${ride.id}`} onClick={book} disabled={busy}
            className="btn-primary rounded-full h-9 px-4 text-sm flex items-center gap-1.5 disabled:opacity-50 ml-auto">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Coins size={14} />} Book seat(s)
          </button>
        </div>
      )}
      {ride.is_mine && <div className="text-[11px] text-brand mt-2">This is your ride offer.</div>}
    </div>
  );
}

function ChatModal({ mission, onClose }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef();
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/transport/missions/${mission.id}/chat`); setMsgs(data.messages); } catch {}
  }, [mission.id]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const send = async () => {
    if (!text.trim()) return;
    const t = text; setText("");
    try { await api.post(`/transport/missions/${mission.id}/chat`, { text: t }); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const other = mission.i_am === "driver" ? mission.rider_name : mission.driver_name;
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4" onClick={onClose} data-testid="chat-modal">
      <div className="rud-card rounded-2xl w-full max-w-md h-[70vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-rud">
          <div className="text-sm font-medium flex items-center gap-2"><MessageCircle size={15} className="text-brand" /> Mission chat · {other}</div>
          <button onClick={onClose} data-testid="chat-close"><X size={18} className="text-sec" /></button>
        </div>
        <div className="flex-1 overflow-auto p-4 space-y-2">
          {msgs.length === 0 && <div className="text-xs text-sec text-center mt-8">Share pickup details, coordinate & say hello 👋</div>}
          {msgs.map((m) => (
            <div key={m.id} className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.user_id === mission[`${mission.i_am}_id`] ? "bg-brand text-black ml-auto" : "bg-elevated border border-rud"}`}>
              <div className="text-[10px] opacity-70">{m.name}</div>{m.text}
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="p-3 border-t border-rud flex items-center gap-2">
          <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Type a message…" className="rud-input flex-1 rounded-full px-4 py-2 text-sm" />
          <button data-testid="chat-send" onClick={send} className="btn-primary rounded-full w-10 h-10 grid place-items-center"><Send size={15} /></button>
        </div>
      </div>
    </div>
  );
}

function ReviewModal({ mission, onClose, onDone }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/transport/missions/${mission.id}/review`, { rating, comment }); toast.success("Thanks — your review keeps the network safe & kind 🙏"); onDone?.(); onClose(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const other = mission.i_am === "driver" ? mission.rider_name : mission.driver_name;
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4" onClick={onClose} data-testid="review-modal">
      <div className="rud-card rounded-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
        <div className="text-sm font-medium">Rate your {mission.i_am === "driver" ? "rider" : "driver"} · {other}</div>
        <div className="flex items-center gap-1.5 my-4 justify-center" data-testid="review-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} onClick={() => setRating(n)} data-testid={`review-star-${n}`}>
              <Star size={30} className={n <= rating ? "text-brand" : "text-sec"} fill={n <= rating ? "currentColor" : "none"} />
            </button>
          ))}
        </div>
        <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="A word on the trip (optional)"
          className="rud-input w-full rounded-lg px-3 py-2 text-sm h-20" />
        <button data-testid="review-submit" onClick={submit} disabled={busy} className="btn-primary w-full rounded-full h-10 mt-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Star size={15} />} Submit review
        </button>
      </div>
    </div>
  );
}

function MissionCard({ m, onAction, onChat, onReview }) {
  const st = STATUS_STYLE[m.status] || STATUS_STYLE.pending;
  const other = m.i_am === "driver" ? { name: m.rider_name, role: "rider" } : { name: m.driver_name, role: "driver" };
  return (
    <div className="rud-card rounded-xl p-4" data-testid={`mission-${m.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium"><Navigation size={14} className="text-brand shrink-0" />
            <span className="truncate">{m.origin.label.split(",")[0]} → {m.destination.label.split(",")[0]}</span></div>
          <div className="text-xs text-sec mt-1 flex flex-wrap items-center gap-x-3">
            <span className="flex items-center gap-1"><Clock size={12} /> {m.date}</span>
            <span className="flex items-center gap-1"><Users size={12} /> {m.seats} seat(s)</span>
            <span className="capitalize">You: {m.i_am} · with {other.name}</span>
          </div>
        </div>
        <span className="text-[11px] px-2.5 py-1 rounded-full font-medium" style={{ background: `${st.c}22`, color: st.c }} data-testid={`mission-status-${m.id}`}>{st.label}</span>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 text-center">
        <div><div className="text-[10px] uppercase tracking-widest text-sec">Fare</div><Price usd={m.fare_credits} primaryClass="text-sm font-serif" showSub={false} /></div>
        <div><div className="text-[10px] uppercase tracking-widest text-sec">Driver gets</div><Price usd={m.driver_earn} primaryClass="text-sm font-serif text-brand" showSub={false} /></div>
        <div><div className="text-[10px] uppercase tracking-widest text-sec">My Kind fee</div><Price usd={m.platform_fee} primaryClass="text-sm font-serif" showSub={false} /></div>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {m.status === "pending" && m.i_am === "driver" && (
          <button data-testid={`confirm-${m.id}`} onClick={() => onAction(m, "confirm")} className="btn-primary rounded-full h-9 px-4 text-sm flex items-center gap-1.5"><CheckCircle2 size={14} /> Confirm</button>
        )}
        {m.status === "confirmed" && m.i_am === "driver" && (
          <button data-testid={`start-${m.id}`} onClick={() => onAction(m, "start")} className="btn-primary rounded-full h-9 px-4 text-sm flex items-center gap-1.5"><Play size={14} /> Start</button>
        )}
        {(m.status === "confirmed" || m.status === "in_progress") && (
          <button data-testid={`complete-${m.id}`} onClick={() => onAction(m, "complete")} className="btn-primary rounded-full h-9 px-4 text-sm flex items-center gap-1.5"><Flag size={14} /> Mission complete</button>
        )}
        {(m.status === "pending" || m.status === "confirmed") && (
          <button data-testid={`cancel-${m.id}`} onClick={() => onAction(m, "cancel")} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5"><X size={14} /> Cancel</button>
        )}
        {["confirmed", "in_progress", "completed"].includes(m.status) && (
          <button data-testid={`chat-${m.id}`} onClick={() => onChat(m)} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5"><MessageCircle size={14} /> Chat</button>
        )}
        {m.status === "completed" && !m.reviewed_by_me && (
          <button data-testid={`review-${m.id}`} onClick={() => onReview(m)} className="btn-primary rounded-full h-9 px-4 text-sm flex items-center gap-1.5"><Star size={14} /> Rate</button>
        )}
        {m.status === "completed" && m.reviewed_by_me && <span className="text-[11px] text-brand flex items-center gap-1 self-center"><CheckCircle2 size={12} /> Reviewed</span>}
      </div>
    </div>
  );
}

const TABS = [
  { id: "find", label: "Find a ride", icon: Search },
  { id: "offer", label: "Offer a ride", icon: Car },
  { id: "tours", label: "Tours", icon: Globe },
  { id: "stays", label: "Stays", icon: Home },
  { id: "matches", label: "Matches", icon: Sparkles },
  { id: "missions", label: "My trips", icon: Route },
];

const EMPTY_TOUR = { title: "", summary: "", destination_label: "", start_date: "", cost_per_person: "", max_joiners: 8, itinerary: [{ city: "", activities: "" }] };

function ToursPanel() {
  const { user, setUser } = useApp();
  const [view, setView] = useState("browse");
  const [tours, setTours] = useState([]);
  const [mine, setMine] = useState({ organized: [], joined: [] });
  const [form, setForm] = useState(EMPTY_TOUR);
  const [busy, setBusy] = useState(false);
  const [joinBusy, setJoinBusy] = useState("");
  const [joinersFor, setJoinersFor] = useState(null);
  const [joiners, setJoiners] = useState([]);
  const [chatTour, setChatTour] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState("");

  const openChat = async (t) => {
    setChatTour(t); setMessages([]);
    try { const { data } = await api.get(`/transport/tours/${t.id}/chat`); setMessages(data.messages || []); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setChatTour(null); }
  };
  const sendMsg = async () => {
    if (!msgText.trim() || !chatTour) return;
    try { const { data } = await api.post(`/transport/tours/${chatTour.id}/chat`, { text: msgText }); setMessages((m) => [...m, data]); setMsgText(""); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const loadTours = useCallback(() => { api.get("/transport/tours").then(({ data }) => setTours(data.tours || [])).catch(() => {}); }, []);
  const loadMine = useCallback(() => { api.get("/transport/tours/mine").then(({ data }) => setMine(data)).catch(() => {}); }, []);
  useEffect(() => { loadTours(); }, [loadTours]);
  useEffect(() => { if (view === "mine") loadMine(); }, [view, loadMine]);

  const setDay = (i, k, v) => setForm((f) => ({ ...f, itinerary: f.itinerary.map((d, j) => (j === i ? { ...d, [k]: v } : d)) }));
  const addDay = () => setForm((f) => ({ ...f, itinerary: [...f.itinerary, { city: "", activities: "" }] }));
  const removeDay = (i) => setForm((f) => ({ ...f, itinerary: f.itinerary.filter((_, j) => j !== i) }));

  const create = async () => {
    if (!form.title.trim()) { toast.error("Give your tour a title."); return; }
    setBusy(true);
    try {
      await api.post("/transport/tours", { ...form, cost_per_person: Number(form.cost_per_person) || 0, max_joiners: Number(form.max_joiners) || 8 });
      toast.success("Tour published! Members can now join.");
      setForm(EMPTY_TOUR); setView("mine"); loadTours(); loadMine();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const join = async (t) => {
    setJoinBusy(t.id);
    try {
      const { data } = await api.post(`/transport/tours/${t.id}/join`);
      toast.success(`Joined! ${data.charged} credits paid to the organizer.`);
      if (setUser) setUser((u) => (u ? { ...u, credits: data.credits_remaining } : u));
      loadTours();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setJoinBusy(""); }
  };
  const viewJoiners = async (t) => {
    setJoinersFor(t);
    try { const { data } = await api.get(`/transport/tours/${t.id}/joiners`); setJoiners(data.joiners || []); } catch { setJoiners([]); }
  };
  const closeTour = async (t) => { try { await api.post(`/transport/tours/${t.id}/close`); toast.success("Tour closed."); loadMine(); loadTours(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  const TourCard = ({ t, own }) => (
    <div className="rud-card rounded-2xl p-5" data-testid={`tour-${t.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-serif text-lg leading-tight">{t.title}</div>
          <div className="text-[11px] text-sec flex flex-wrap items-center gap-x-2 mt-0.5">
            {t.destination_label && <span className="flex items-center gap-0.5"><MapPin size={11} /> {t.destination_label}</span>}
            {t.start_date && <span className="flex items-center gap-0.5"><Clock size={11} /> {t.start_date}</span>}
            <span>{t.days} day{t.days === 1 ? "" : "s"}</span>
            <span>by {t.organizer_name}</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-serif text-xl text-brand flex items-center gap-1 justify-end"><Coins size={14} /> {t.cost_per_person}</div>
          <div className="text-[10px] text-sec">per person</div>
        </div>
      </div>
      {t.summary && <p className="text-xs text-sec mt-2">{t.summary}</p>}
      {t.itinerary?.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {t.itinerary.map((d) => (
            <div key={d.day} className="flex gap-2 text-xs">
              <span className="shrink-0 w-14 text-brand font-medium">Day {d.day}</span>
              <span className="min-w-0"><b className="text-[var(--rud-text)]">{d.city}</b>{d.activities ? ` — ${d.activities}` : ""}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-rud text-[11px] text-sec">
        <span className="flex items-center gap-1"><UsersRound size={12} /> {t.joiners_count}/{t.max_joiners} joined</span>
        <span className="ml-auto">My Kind fee {t.fee_pct}%</span>
        {own ? (
          <>
            <button data-testid={`tour-joiners-${t.id}`} onClick={() => viewJoiners(t)} className="btn-ghost rounded-full h-8 px-3 text-xs">Joiners</button>
            <button data-testid={`tour-chat-${t.id}`} onClick={() => openChat(t)} className="btn-ghost rounded-full h-8 px-3 text-xs flex items-center gap-1"><Send size={12} /> Chat</button>
            {t.status === "open" && <button onClick={() => closeTour(t)} className="text-[11px] hover:text-red-400">Close</button>}
          </>
        ) : (
          <button data-testid={`tour-join-${t.id}`} onClick={() => join(t)} disabled={joinBusy === t.id || t.joiners_count >= t.max_joiners}
            className="btn-primary h-8 px-4 rounded-full text-xs flex items-center gap-1.5 disabled:opacity-50">
            {joinBusy === t.id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Join · {t.cost_per_person} cr
          </button>
        )}
        {t.destination_label && <a href={`/transport?tab=stays&city=${encodeURIComponent(t.destination_label)}`} data-testid={`tour-stays-${t.id}`} className="btn-ghost rounded-full h-8 px-3 text-xs flex items-center gap-1"><Home size={12} /> Find stays</a>}
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="tours-panel">
      <div className="flex items-center gap-1 p-0.5 rounded-full bg-elevated border border-rud text-[11px] w-fit">
        {[["browse", "Browse tours"], ["create", "Plan a tour"], ["mine", "My tours"]].map(([k, lbl]) => (
          <button key={k} data-testid={`tourtab-${k}`} onClick={() => setView(k)} className={`px-3 py-1 rounded-full transition-colors ${view === k ? "bg-brand text-black" : "text-sec hover:text-brand"}`}>{lbl}</button>
        ))}
      </div>

      {view === "browse" && (tours.length === 0 ? <div className="rud-card rounded-xl p-6 text-center text-sec text-sm">No tours yet. Plan the first one and invite members to join!</div>
        : <div className="grid sm:grid-cols-2 gap-3">{tours.map((t) => <TourCard key={t.id} t={t} own={t.is_mine} />)}</div>)}

      {view === "create" && (
        <div className="rud-card rounded-2xl p-6 max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-brand mb-3 flex items-center gap-1.5"><Globe size={14} /> Plan a community tour</div>
          <input data-testid="tour-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Tour title (e.g. Himalayan 5-Day Trek)" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mb-2" />
          <input data-testid="tour-destination" value={form.destination_label} onChange={(e) => setForm({ ...form, destination_label: e.target.value })} placeholder="Destination / region" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mb-2" />
          <textarea data-testid="tour-summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={2} placeholder="Short description of the trip…" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm mb-2" />
          <div className="grid grid-cols-3 gap-2 mb-3">
            <input data-testid="tour-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} placeholder="Start date" className="rud-input rounded-lg px-3 py-2.5 text-sm" />
            <input data-testid="tour-cost" type="number" min="0" value={form.cost_per_person} onChange={(e) => setForm({ ...form, cost_per_person: e.target.value })} placeholder="Cost/person (cr)" className="rud-input rounded-lg px-3 py-2.5 text-sm" />
            <input data-testid="tour-max" type="number" min="1" value={form.max_joiners} onChange={(e) => setForm({ ...form, max_joiners: e.target.value })} placeholder="Max joiners" className="rud-input rounded-lg px-3 py-2.5 text-sm" />
          </div>
          <div className="text-[11px] uppercase tracking-widest text-sec mb-2">Day-by-day itinerary</div>
          <div className="space-y-2">
            {form.itinerary.map((d, i) => (
              <div key={i} className="flex gap-2 items-start" data-testid={`tour-day-${i}`}>
                <span className="shrink-0 w-12 h-10 grid place-items-center rounded-lg bg-brand/15 text-brand text-xs font-medium">D{i + 1}</span>
                <input data-testid={`tour-day-city-${i}`} value={d.city} onChange={(e) => setDay(i, "city", e.target.value)} placeholder="City" className="rud-input w-32 rounded-lg px-3 py-2.5 text-sm" />
                <input data-testid={`tour-day-act-${i}`} value={d.activities} onChange={(e) => setDay(i, "activities", e.target.value)} placeholder="Activities (e.g. temple visit, hike, local food)" className="rud-input flex-1 rounded-lg px-3 py-2.5 text-sm" />
                {form.itinerary.length > 1 && <button onClick={() => removeDay(i)} className="shrink-0 h-10 w-9 grid place-items-center text-sec hover:text-red-400"><X size={15} /></button>}
              </div>
            ))}
          </div>
          <button data-testid="tour-add-day" onClick={addDay} className="btn-ghost rounded-full h-8 px-4 text-xs mt-2 flex items-center gap-1.5"><MapPin size={13} /> Add day</button>
          <div className="mt-4"><button data-testid="tour-create" onClick={create} disabled={busy} className="btn-primary h-11 px-6 flex items-center gap-2 disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : <Globe size={16} />} Publish tour</button></div>
        </div>
      )}

      {view === "mine" && (
        <div className="space-y-5">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-brand mb-2">Tours you organize</div>
            {mine.organized.length === 0 ? <p className="text-sm text-sec">You haven't planned any tours yet.</p> : <div className="grid sm:grid-cols-2 gap-3">{mine.organized.map((t) => <TourCard key={t.id} t={t} own />)}</div>}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-brand mb-2">Tours you joined</div>
            {mine.joined.length === 0 ? <p className="text-sm text-sec">You haven't joined any tours yet.</p> : (
              <div className="space-y-2">{mine.joined.map((j) => (
                <div key={j.id} className="flex items-center gap-3 rounded-lg border border-rud px-3 py-2 text-sm">
                  <Globe size={14} className="text-brand shrink-0" />
                  <span className="flex-1 truncate">{j.tour?.title || "Tour"}</span>
                  <span className="text-[11px] text-sec">{j.cost} cr</span>
                  {j.tour && <button data-testid={`tour-chat-joined-${j.tour.id}`} onClick={() => openChat(j.tour)} className="btn-ghost rounded-full h-7 px-3 text-xs flex items-center gap-1"><Send size={11} /> Chat</button>}
                </div>
              ))}</div>
            )}
          </div>
        </div>
      )}

      {joinersFor && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setJoinersFor(null)} data-testid="tour-joiners-modal">
          <div className="rud-card rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between"><h3 className="font-serif text-xl">Joiners · {joinersFor.title}</h3><button onClick={() => setJoinersFor(null)} className="text-sec hover:text-brand"><X size={20} /></button></div>
            {joiners.length === 0 ? <p className="text-sm text-sec mt-3">No joiners yet.</p> : (
              <div className="space-y-2 mt-3">{joiners.map((j) => (
                <div key={j.id} className="flex items-center gap-3 rounded-lg border border-rud px-3 py-2 text-sm"><UsersRound size={14} className="text-brand" /><span className="flex-1">{j.joiner_name}</span><span className="text-[11px] text-brand">+{j.organizer_earn} cr</span></div>
              ))}</div>
            )}
          </div>
        </div>
      )}
      {chatTour && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setChatTour(null)} data-testid="tour-chat-modal">
          <div className="rud-card rounded-2xl p-5 max-w-lg w-full flex flex-col" style={{ height: "70vh" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3"><h3 className="font-serif text-lg">{chatTour.title} · Trip chat</h3><button onClick={() => setChatTour(null)} className="text-sec hover:text-brand"><X size={20} /></button></div>
            <div className="flex-1 overflow-auto space-y-2 pr-1" data-testid="tour-chat-messages">
              {messages.length === 0 ? <p className="text-sm text-sec text-center mt-6">No messages yet — say hello 👋</p> : messages.map((m) => (
                <div key={m.id} className="rounded-lg border border-rud px-3 py-2 text-sm">
                  <span className="text-brand text-xs">{m.user_name}</span>
                  <p className="text-[var(--rud-text)]">{m.text}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <input data-testid="tour-chat-input" value={msgText} onChange={(e) => setMsgText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()} placeholder="Message the group…" className="rud-input flex-1 rounded-lg px-3 py-2.5 text-sm" />
              <button data-testid="tour-chat-send" onClick={sendMsg} className="btn-primary h-11 px-4 rounded-lg text-sm flex items-center gap-1.5"><Send size={15} /></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const EMPTY_STAY = { home_name: "", city_label: "", accommodation_type: "private_room", food: "none", food_type: "", max_guests: 2, bedrooms: 1, bathrooms: 1, beds: 1, amenities: [], price_per_night: "", house_rules: "", summary: "", photos: [], instant_book: false };

function ProfileChip({ p, rating }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      {p?.avatar_url ? <img src={p.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" /> : <div className="w-8 h-8 rounded-full bg-brand/15 grid place-items-center text-brand shrink-0"><Users size={14} /></div>}
      <div className="min-w-0">
        <div className="text-sm font-medium truncate flex items-center gap-1">{p?.name || "Member"}
          {rating?.superhost && <span className="text-[9px] text-brand border border-brand/50 rounded-full px-1.5 py-0 flex items-center gap-0.5"><Star size={8} className="fill-brand" /> Superhost</span>}
        </div>
        <div className="text-[10px] text-sec truncate flex items-center gap-1">
          {rating?.count > 0 ? <><Star size={9} className="text-brand fill-brand" /> {rating.score} ({rating.count})</> : "New member"}
          {p?.location ? ` · ${p.location}` : ""}
        </div>
      </div>
    </div>
  );
}

function StayReviewModal({ booking, role, onClose, onDone }) {
  const [rating, setRating] = useState(5);
  const [cleanliness, setCleanliness] = useState(5);
  const [communication, setCommunication] = useState(5);
  const [comment, setComment] = useState("");
  const [recommend, setRecommend] = useState(true);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/transport/stays/bookings/${booking.id}/review`, { rating, cleanliness, communication, comment, recommend });
      toast.success("Review submitted — thank you for keeping the community trusted! 🌟");
      onDone?.(); onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const Row = ({ label, val, setVal, testId }) => (
    <div className="flex items-center justify-between">
      <span className="text-sm text-sec">{label}</span>
      <div className="flex gap-1" data-testid={testId}>
        {[1, 2, 3, 4, 5].map((s) => (
          <button key={s} onClick={() => setVal(s)} type="button"><Star size={18} className={s <= val ? "text-brand fill-brand" : "text-sec"} /></button>
        ))}
      </div>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose} data-testid="stay-review-modal">
      <div className="rud-card rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div><h3 className="font-serif text-xl">Review your {role === "host" ? "guest" : "stay"}</h3><p className="text-xs text-sec mt-0.5">{booking.home_name}</p></div>
          <button onClick={onClose} className="text-sec hover:text-brand"><X size={20} /></button>
        </div>
        <div className="space-y-3 mt-4">
          <Row label="Overall" val={rating} setVal={setRating} testId="review-rating" />
          {role === "guest" && <Row label="Cleanliness" val={cleanliness} setVal={setCleanliness} testId="review-cleanliness" />}
          <Row label="Communication" val={communication} setVal={setCommunication} testId="review-communication" />
          <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder={role === "host" ? "How was hosting this guest?" : "Share your experience of this home & host…"} className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
          <label className="flex items-center gap-2 text-sm text-sec cursor-pointer">
            <input type="checkbox" data-testid="review-recommend" checked={recommend} onChange={(e) => setRecommend(e.target.checked)} />
            <ThumbsUp size={14} className="text-brand" /> I'd {role === "host" ? "host this guest" : "stay here"} again
          </label>
          <button data-testid="review-submit" onClick={submit} disabled={busy} className="btn-primary w-full h-10 rounded-full text-sm flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Star size={15} />} Submit review
          </button>
        </div>
      </div>
    </div>
  );
}

const STAY_STATUS_LOCAL_REMOVED = true;

function StayChatModal({ booking, onClose }) {
  const [msgs, setMsgs] = useState([]);
  const [me, setMe] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/transport/stays/bookings/${booking.id}/chat`).then(({ data }) => { setMsgs(data.messages); setMe(data.me); }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/transport/stays/bookings/${booking.id}/chat`, { text }); setMsgs((m) => [...m, data.message]); setText(""); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={onClose} data-testid="stay-chat-modal">
      <div className="rud-card rounded-2xl p-6 max-w-lg w-full flex flex-col max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div><h3 className="font-serif text-xl">Chat</h3><p className="text-xs text-sec mt-0.5">{booking.home_name}</p></div>
          <button onClick={onClose} className="text-sec hover:text-brand"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-auto my-3 space-y-2 min-h-[120px]" data-testid="stay-chat-list">
          {msgs.length === 0 ? <p className="text-sm text-sec text-center py-6">No messages yet — say hello 👋</p>
            : msgs.map((m) => (
              <div key={m.id} className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.sender_id === me ? "ml-auto bg-brand text-black" : "bg-elevated border border-rud"}`}>
                <div className="text-[10px] opacity-70 mb-0.5">{m.sender_name}</div>{m.text}
              </div>
            ))}
        </div>
        <div className="flex items-center gap-2">
          <input data-testid="stay-chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Coordinate arrival, questions…" className="rud-input flex-1 rounded-full px-4 py-2.5 text-sm" />
          <button data-testid="stay-chat-send" onClick={send} disabled={busy || !text.trim()} className="btn-primary rounded-full h-10 w-10 flex items-center justify-center shrink-0 disabled:opacity-50">{busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}</button>
        </div>
      </div>
    </div>
  );
}

function StaysPanel() {
  const { user, setUser } = useApp();
  const nav = useNavigate();
  const [view, setView] = useState("guest");
  const [listings, setListings] = useState([]);
  const [mine, setMine] = useState({ host_listings: [], incoming: [], outgoing: [] });
  const [form, setForm] = useState(EMPTY_STAY);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [actBusy, setActBusy] = useState("");
  const [reviewFor, setReviewFor] = useState(null);
  const [filters, setFilters] = useState({ city: "", max_price: "", food: "", amenities: [], superhost: false });
  const [showFilters, setShowFilters] = useState(false);
  const [chatFor, setChatFor] = useState(null);
  const [params] = useSearchParams();
  const [savedView, setSavedView] = useState(false);
  const [saved, setSaved] = useState([]);

  const loadSaved = useCallback(() => {
    if (user) api.get("/transport/stays/wishlist").then(({ data }) => setSaved(data.listings || [])).catch(() => {});
  }, [user]);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const toggleWishlist = async (l, e) => {
    if (e) e.stopPropagation();
    if (!user) { toast.error("Sign in to save homes to your wishlist."); return; }
    try {
      const { data } = await api.post(`/transport/stays/${l.id}/wishlist`);
      const flip = (arr) => arr.map((x) => x.id === l.id ? { ...x, is_wishlisted: data.wishlisted } : x);
      setListings((arr) => flip(arr));
      if (data.wishlisted) { setSaved((arr) => arr.some((x) => x.id === l.id) ? flip(arr) : [{ ...l, is_wishlisted: true }, ...arr]); }
      else { setSaved((arr) => arr.filter((x) => x.id !== l.id)); }
      toast.success(data.wishlisted ? "Saved to your wishlist ❤️" : "Removed from wishlist");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const loadListings = useCallback(() => {
    const p = { q };
    if (filters.city) p.city = filters.city;
    if (filters.max_price) p.max_price = filters.max_price;
    if (filters.food) p.food = filters.food;
    if (filters.amenities.length) p.amenities = filters.amenities.join(",");
    if (filters.superhost) p.superhost = 1;
    return api.get("/transport/stays", { params: p }).then(({ data }) => setListings(data.listings)).catch(() => {});
  }, [q, filters]);
  const loadMine = useCallback(() => { if (user) api.get("/transport/stays/mine").then(({ data }) => setMine(data)).catch(() => {}); }, [user]);
  useEffect(() => { loadListings(); }, [loadListings]);
  useEffect(() => { loadMine(); }, [loadMine]);
  useEffect(() => { const c = params.get("city"); if (c) { setFilters((f) => ({ ...f, city: c })); setShowFilters(true); } }, [params]);
  const toggleFilterAmenity = (k) => setFilters((f) => ({ ...f, amenities: f.amenities.includes(k) ? f.amenities.filter((x) => x !== k) : [...f.amenities, k] }));

  const host = async () => {
    if (!form.home_name.trim()) { toast.error("Give your home a name."); return; }
    if (!form.city_label.trim()) { toast.error("Add your home's city/location."); return; }
    setBusy(true);
    try {
      await api.post("/transport/stays", { ...form, price_per_night: Number(form.price_per_night) || 0, max_guests: Number(form.max_guests) || 1, bedrooms: Number(form.bedrooms) || 0, bathrooms: Number(form.bathrooms) || 0, beds: Number(form.beds) || 0 });
      toast.success("Your home is listed! Travelers can now find & request it. 🏡");
      setForm(EMPTY_STAY); loadMine(); loadListings();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const toggleAmenity = (k) => setForm((s) => ({ ...s, amenities: s.amenities.includes(k) ? s.amenities.filter((x) => x !== k) : [...s.amenities, k] }));
  const closeListing = async (lid) => {
    try { await api.post(`/transport/stays/${lid}/close`); toast.success("Listing closed"); loadMine(); loadListings(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const act = async (bid, action) => {
    setActBusy(bid + action);
    try {
      const { data } = await api.post(`/transport/stays/bookings/${bid}/${action}`);
      if (action === "pay" && setUser) setUser((u) => u ? { ...u, credits: data.credits_remaining } : u);
      const msg = { accept: "Request accepted — awaiting guest payment", decline: "Request declined", pay: "Payment sent — stay confirmed! 🎉", complete: "Marked complete", cancel: "Cancelled" }[action];
      toast.success(msg); loadMine();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setActBusy(""); }
  };

  const upcoming = mine.outgoing.filter(isUpcomingStay);
  const past = mine.outgoing.filter((b) => !isUpcomingStay(b));

  const guestRow = (b) => (
    <div key={b.id} data-testid={`outgoing-${b.id}`} className="rud-card rounded-xl p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <ProfileChip p={b.host} rating={b.host_rating} />
        <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ color: STAY_STATUS[b.status]?.c, border: `1px solid ${STAY_STATUS[b.status]?.c}55` }}>{STAY_STATUS[b.status]?.label}</span>
      </div>
      <div className="text-xs text-sec mt-2">{b.home_name} · {b.nights} night(s){b.check_in ? ` from ${b.check_in}` : ""} · <Price usd={b.total_cost} inline showSub={false} /></div>
      <div className="flex flex-wrap gap-2 mt-3">
        {b.status === "accepted" && <button data-testid={`pay-${b.id}`} onClick={() => act(b.id, "pay")} disabled={actBusy === b.id + "pay"} className="btn-primary rounded-full h-8 px-3 text-xs flex items-center gap-1"><Coins size={12} /> Pay {b.total_cost} & confirm</button>}
        {["accepted", "paid", "completed"].includes(b.status) && <button data-testid={`gchat-${b.id}`} onClick={() => setChatFor(b)} className="btn-ghost rounded-full h-8 px-3 text-xs flex items-center gap-1"><MessageCircle size={12} /> Chat</button>}
        {(b.status === "requested" || b.status === "accepted") && <button data-testid={`cancel-${b.id}`} onClick={() => act(b.id, "cancel")} className="btn-ghost rounded-full h-8 px-3 text-xs">Cancel</button>}
        {b.status === "paid" && <button data-testid={`gcomplete-${b.id}`} onClick={() => act(b.id, "complete")} className="btn-ghost rounded-full h-8 px-3 text-xs">Mark complete</button>}
        {(b.status === "paid" || b.status === "completed") && !b.reviewed && <button data-testid={`review-host-${b.id}`} onClick={() => setReviewFor({ b, role: "guest" })} className="btn-primary rounded-full h-8 px-3 text-xs flex items-center gap-1"><Star size={12} /> Review stay</button>}
      </div>
    </div>
  );
  const hostRow = (b) => (
    <div key={b.id} data-testid={`incoming-${b.id}`} className="rud-card rounded-xl p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <ProfileChip p={b.guest} rating={b.guest_rating} />
        <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ color: STAY_STATUS[b.status]?.c, border: `1px solid ${STAY_STATUS[b.status]?.c}55` }}>{STAY_STATUS[b.status]?.label}</span>
      </div>
      <div className="text-xs text-sec mt-2">{b.home_name} · {b.nights} night(s){b.check_in ? ` from ${b.check_in}` : ""} · {b.guests_count} guest(s) · <Price usd={b.total_cost} inline showSub={false} /></div>
      {b.travelers_desc && <div className="text-xs mt-1 text-sec"><b className="text-[var(--rud-text)]">Travellers:</b> {b.travelers_desc}</div>}
      {b.message && <div className="text-xs mt-1 italic text-sec">“{b.message}”</div>}
      <div className="flex flex-wrap gap-2 mt-3">
        {b.status === "requested" && <>
          <button data-testid={`accept-${b.id}`} onClick={() => act(b.id, "accept")} disabled={actBusy === b.id + "accept"} className="btn-primary rounded-full h-8 px-3 text-xs flex items-center gap-1"><CheckCircle2 size={12} /> Accept</button>
          <button data-testid={`decline-${b.id}`} onClick={() => act(b.id, "decline")} className="btn-ghost rounded-full h-8 px-3 text-xs">Decline</button>
        </>}
        {b.status === "paid" && <button data-testid={`complete-${b.id}`} onClick={() => act(b.id, "complete")} className="btn-primary rounded-full h-8 px-3 text-xs">Mark complete</button>}
        {["accepted", "paid", "completed"].includes(b.status) && <button data-testid={`hchat-${b.id}`} onClick={() => setChatFor(b)} className="btn-ghost rounded-full h-8 px-3 text-xs flex items-center gap-1"><MessageCircle size={12} /> Chat</button>}
        {(b.status === "paid" || b.status === "completed") && !b.reviewed && <button data-testid={`review-guest-${b.id}`} onClick={() => setReviewFor({ b, role: "host" })} className="btn-ghost rounded-full h-8 px-3 text-xs flex items-center gap-1"><Star size={12} /> Review guest</button>}
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="stays-panel">
      <div className="rud-card rounded-2xl p-5">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><Home size={14} /> Community Stays</div>
        <p className="text-sec text-sm mt-1.5">Be hosted by community families anywhere in the world — or open your home to travelers. Profiles &amp; interests are shared both ways so you can choose your match. Settled in credits; My Kind keeps just {mine?.host_listings?.[0]?.fee_pct || 6}%.</p>
      </div>

      <div className="flex flex-wrap gap-1 rud-card rounded-full p-1 text-sm w-fit">
        {[["guest", "Be a Guest"], ["host", "Be a Host"]].map(([k, lbl]) => (
          <button key={k} data-testid={`staytab-${k}`} onClick={() => setView(k)} className={`px-4 py-1.5 rounded-full transition-colors ${view === k ? "bg-brand text-black" : "text-sec hover:text-brand"}`}>{lbl}</button>
        ))}
      </div>

      {view === "guest" && (
        <div className="space-y-5">
          <div className="flex items-center gap-2">
            <input data-testid="stay-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by city or home…" className="rud-input rounded-lg px-3 py-2 text-sm flex-1" />
            <button data-testid="stay-saved-toggle" onClick={() => setSavedView((s) => !s)} className={`btn-ghost rounded-full h-9 px-3 text-xs flex items-center gap-1 ${savedView ? "text-rose-400" : ""}`}><Heart size={13} className={savedView ? "fill-rose-400" : ""} /> Saved{saved.length ? ` (${saved.length})` : ""}</button>
            <button data-testid="stay-filter-toggle" onClick={() => setShowFilters((s) => !s)} className={`btn-ghost rounded-full h-9 px-3 text-xs flex items-center gap-1 ${showFilters ? "text-brand" : ""}`}><Search size={13} /> Filters</button>
            <button onClick={loadListings} className="btn-ghost rounded-full w-9 h-9 grid place-items-center"><RefreshCw size={14} /></button>
          </div>
          {showFilters && !savedView && (
            <div className="rud-card rounded-xl p-4 space-y-3" data-testid="stay-filters">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <input data-testid="filter-city" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} placeholder="City" className="rud-input rounded-lg px-3 py-2 text-sm" />
                <input data-testid="filter-price" type="number" min={0} value={filters.max_price} onChange={(e) => setFilters({ ...filters, max_price: e.target.value })} placeholder="Max price/night" className="rud-input rounded-lg px-3 py-2 text-sm" />
                <select data-testid="filter-food" translate="no" value={filters.food} onChange={(e) => setFilters({ ...filters, food: e.target.value })} className="rud-input rounded-lg px-3 py-2 text-sm">
                  <option value="">Any food</option>
                  {Object.entries(FOOD_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <label className="flex items-center gap-2 text-xs text-sec px-2"><input type="checkbox" data-testid="filter-superhost" checked={filters.superhost} onChange={(e) => setFilters({ ...filters, superhost: e.target.checked })} /> Superhosts only</label>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-sec mb-1.5">Amenities</div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(AMENITIES).slice(0, 12).map(([k, v]) => (
                    <button key={k} data-testid={`filter-amenity-${k}`} onClick={() => toggleFilterAmenity(k)} className={`text-[11px] px-2.5 py-1 rounded-full border ${filters.amenities.includes(k) ? "border-brand text-brand bg-brand/5" : "border-rud text-sec"}`}>{v}</button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <button data-testid="filter-apply" onClick={loadListings} className="btn-primary rounded-full h-8 px-4 text-xs">Apply</button>
                <button data-testid="filter-clear" onClick={() => { setFilters({ city: "", max_price: "", food: "", amenities: [], superhost: false }); }} className="btn-ghost rounded-full h-8 px-4 text-xs">Clear</button>
              </div>
            </div>
          )}
          {(() => { const shown = savedView ? saved : listings; return shown.length === 0 ? (
            savedView
              ? <div className="rud-card rounded-xl p-6 text-center text-sec text-sm" data-testid="wishlist-empty">No saved homes yet. Tap the <Heart size={12} className="inline text-rose-400" /> on any home to save it for later.</div>
              : <div className="rud-card rounded-xl p-6 text-center text-sec text-sm">No homes listed yet. <button onClick={() => setView("host")} className="text-brand underline">Be a Host</button> and open yours.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid={savedView ? "wishlist-grid" : "stays-grid"}>
              {shown.map((l) => (
                <div key={l.id} data-testid={`stay-card-${l.id}`} onClick={() => nav(`/stays/${l.id}`)} className="rud-card rounded-2xl overflow-hidden flex flex-col cursor-pointer hover:ring-1 hover:ring-brand/40">
                  <div className="relative">
                    {l.photos?.[0] ? <img src={l.photos[0]} alt={l.home_name} className="w-full h-36 object-cover" /> : <div className="w-full h-36 bg-brand/10 grid place-items-center text-brand"><Home size={30} /></div>}
                    <button data-testid={`wishlist-${l.id}`} onClick={(e) => toggleWishlist(l, e)} title={l.is_wishlisted ? "Remove from wishlist" : "Save to wishlist"}
                      className="absolute top-2 right-2 w-9 h-9 grid place-items-center rounded-full bg-black/45 backdrop-blur-sm hover:bg-black/60 transition-colors">
                      <Heart size={17} className={l.is_wishlisted ? "text-rose-500 fill-rose-500" : "text-white"} />
                    </button>
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-serif text-lg truncate">{l.home_name}</div>
                      {l.instant_book && <span className="text-[10px] text-brand border border-brand/40 rounded-full px-2 py-0.5 flex items-center gap-0.5 shrink-0"><Zap size={9} /> Instant</span>}
                    </div>
                    <div className="text-xs text-sec flex items-center gap-1 mt-0.5"><MapPin size={11} /> {l.city_label}</div>
                    <div className="flex flex-wrap gap-1.5 mt-2 text-[11px]">
                      <span className="px-2 py-0.5 rounded-full bg-elevated border border-rud flex items-center gap-1"><BedDouble size={11} /> {ACCOM_LABEL[l.accommodation_type]}</span>
                      <span className="px-2 py-0.5 rounded-full bg-elevated border border-rud flex items-center gap-1"><Utensils size={11} /> {FOOD_LABEL[l.food]}{l.food_type ? ` · ${l.food_type}` : ""}</span>
                      <span className="px-2 py-0.5 rounded-full bg-elevated border border-rud flex items-center gap-1"><Users size={11} /> {l.max_guests}</span>
                    </div>
                    {l.shared_interests?.length > 0 && (
                      <div className="text-[11px] text-brand mt-2 flex items-center gap-1" data-testid={`shared-${l.id}`}><Heart size={11} /> {l.shared_interests.length} shared interest{l.shared_interests.length > 1 ? "s" : ""}: {l.shared_interests.slice(0, 3).join(", ")}</div>
                    )}
                    <div className="mt-3 pt-3 border-t border-rud flex items-center justify-between gap-2">
                      <ProfileChip p={l.host} rating={l.host_rating} />
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="text-sm">{Number(l.price_per_night) > 0 ? <Price usd={l.price_per_night} inline primaryClass="font-serif text-lg" showSub={false} /> : <span className="text-brand font-medium">Free / hospitality</span>}<span className="text-xs text-sec"> / night</span></div>
                      <span className="text-brand text-sm flex items-center gap-1">View <ArrowRight size={13} /></span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ); })()}

          <div>
            <h3 className="font-serif text-lg mb-2 flex items-center gap-2"><CalendarDays size={16} className="text-brand" /> My upcoming stays</h3>
            {upcoming.length === 0 ? <p className="text-sm text-sec">No upcoming stays yet — find a home above.</p> : <div className="space-y-2">{upcoming.map(guestRow)}</div>}
          </div>
          {past.length > 0 && (
            <div>
              <h3 className="font-serif text-lg mb-2 flex items-center gap-2"><Route size={16} className="text-sec" /> Past stays</h3>
              <div className="space-y-2">{past.map(guestRow)}</div>
            </div>
          )}
        </div>
      )}

      {view === "host" && (
        <div className="space-y-5">
          <div className="rud-card rounded-2xl p-5 space-y-3" data-testid="host-form">
            <h3 className="font-serif text-xl">Open your home</h3>
            <input data-testid="stay-home-name" value={form.home_name} onChange={(e) => setForm({ ...form, home_name: e.target.value })} placeholder="Home name (e.g. Sunny Hill Homestay)" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
            <input data-testid="stay-city" value={form.city_label} onChange={(e) => setForm({ ...form, city_label: e.target.value })} placeholder="City / location" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="stay-accom" value={form.accommodation_type} onChange={(e) => setForm({ ...form, accommodation_type: e.target.value })} className="rud-input rounded-lg px-3 py-2 text-sm">
                {Object.entries(ACCOM_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <select data-testid="stay-food" value={form.food} onChange={(e) => setForm({ ...form, food: e.target.value })} className="rud-input rounded-lg px-3 py-2 text-sm">
                {Object.entries(FOOD_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <input data-testid="stay-food-type" value={form.food_type} onChange={(e) => setForm({ ...form, food_type: e.target.value })} placeholder="Food type (veg…)" className="rud-input rounded-lg px-3 py-2 text-sm" />
              <input data-testid="stay-max-guests" type="number" min={1} value={form.max_guests} onChange={(e) => setForm({ ...form, max_guests: e.target.value })} placeholder="Max guests" className="rud-input rounded-lg px-3 py-2 text-sm" />
              <input data-testid="stay-price" type="number" min={0} value={form.price_per_night} onChange={(e) => setForm({ ...form, price_per_night: e.target.value })} placeholder="Price/night (0 = free)" className="rud-input rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <input data-testid="stay-bedrooms" type="number" min={0} value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} placeholder="Bedrooms" className="rud-input rounded-lg px-3 py-2 text-sm" />
              <input data-testid="stay-bathrooms" type="number" min={0} value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} placeholder="Bathrooms" className="rud-input rounded-lg px-3 py-2 text-sm" />
              <input data-testid="stay-beds" type="number" min={0} value={form.beds} onChange={(e) => setForm({ ...form, beds: e.target.value })} placeholder="Beds" className="rud-input rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-sec mb-2">What's available</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5" data-testid="amenities-picker">
                {Object.entries(AMENITIES).map(([k, v]) => (
                  <label key={k} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-lg border cursor-pointer ${form.amenities.includes(k) ? "border-brand text-brand bg-brand/5" : "border-rud text-sec"}`}>
                    <input type="checkbox" data-testid={`amenity-${k}`} checked={form.amenities.includes(k)} onChange={() => toggleAmenity(k)} className="accent-current" /> {v}
                  </label>
                ))}
              </div>
            </div>
            <textarea data-testid="stay-summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={2} placeholder="Describe your home & the experience…" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
            <textarea data-testid="stay-rules" value={form.house_rules} onChange={(e) => setForm({ ...form, house_rules: e.target.value })} rows={2} placeholder="House rules (optional)" className="rud-input w-full rounded-lg px-3 py-2 text-sm" />
            <div className="flex items-center gap-2 flex-wrap">
              {form.photos.map((p, i) => (
                <div key={i} className="relative"><img src={p} alt="" className="w-16 h-16 rounded-lg object-cover" /><button onClick={() => setForm({ ...form, photos: form.photos.filter((_, j) => j !== i) })} className="absolute -top-1 -right-1 bg-black/70 rounded-full text-white"><X size={12} /></button></div>
              ))}
              {form.photos.length < 6 && <FileUpload category="general" accept="image/*" label="Add photo" testId="stay-photo" onUploaded={(f) => setForm((s) => ({ ...s, photos: [...s.photos, f.absoluteUrl] }))} />}
            </div>
            <label className="flex items-center gap-2 text-sm text-sec cursor-pointer">
              <input type="checkbox" data-testid="stay-instant" checked={form.instant_book} onChange={(e) => setForm({ ...form, instant_book: e.target.checked })} />
              <Zap size={14} className="text-brand" /> Instant book (guests pay & book without waiting for my approval)
            </label>
            <button data-testid="stay-host-submit" onClick={host} disabled={busy} className="btn-primary w-full h-10 rounded-full text-sm flex items-center justify-center gap-2 disabled:opacity-50">{busy ? <Loader2 size={15} className="animate-spin" /> : <Home size={15} />} List my home</button>
          </div>

          <div>
            <h3 className="font-serif text-lg mb-2">Requests & bookings on my homes</h3>
            {mine.incoming.length === 0 ? <p className="text-sm text-sec">No requests yet.</p> : <div className="space-y-2">{mine.incoming.map(hostRow)}</div>}
          </div>
          <div>
            <h3 className="font-serif text-lg mb-2">My listed homes</h3>
            {mine.host_listings.length === 0 ? <p className="text-sm text-sec">You haven't listed a home yet.</p> : (
              <div className="space-y-2">{mine.host_listings.map((l) => (
                <div key={l.id} className="rud-card rounded-xl p-3 flex items-center justify-between gap-2" data-testid={`mylisting-${l.id}`}>
                  <div className="text-sm truncate cursor-pointer" onClick={() => nav(`/stays/${l.id}`)}>{l.home_name} <span className="text-xs text-sec">· {l.city_label} · {Number(l.price_per_night) > 0 ? `${l.price_per_night}/night` : "free"}</span></div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[11px] ${l.status === "open" ? "text-brand" : "text-sec"}`}>{l.status}</span>
                    {l.status === "open" && <button data-testid={`close-${l.id}`} onClick={() => closeListing(l.id)} className="text-[11px] text-sec hover:text-red-400 flex items-center gap-1"><Trash2 size={11} /> Delist</button>}
                  </div>
                </div>
              ))}</div>
            )}
          </div>
        </div>
      )}

      {reviewFor && <StayReviewModal booking={reviewFor.b} role={reviewFor.role} onClose={() => setReviewFor(null)} onDone={loadMine} />}
      {chatFor && <StayChatModal booking={chatFor} onClose={() => setChatFor(null)} />}
    </div>
  );
}

export default function MissionTransport() {
  const { user, setUser, country, refreshUser } = useApp();
  const [meta, setMeta] = useState(null);
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "find");
  useEffect(() => { const t = params.get("tab"); if (t) setTab(t); }, [params]);
  const [alerts, setAlerts] = useState({ alerts: [], unread: 0 });
  const [showAlerts, setShowAlerts] = useState(false);
  const [offers, setOffers] = useState([]);
  const [searchDate, setSearchDate] = useState("");
  const [matches, setMatches] = useState([]);
  const [missions, setMissions] = useState([]);
  const [chatMission, setChatMission] = useState(null);
  const [reviewMission, setReviewMission] = useState(null);
  const [verifyFile, setVerifyFile] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const verified = !!meta?.me?.verified;

  const loadMeta = useCallback(() => { api.get("/transport/meta").then(({ data }) => setMeta(data)).catch(() => {}); }, [user]);
  const loadOffers = useCallback(() => { api.get("/transport/search", { params: { date: searchDate } }).then(({ data }) => setOffers(data.rides)).catch(() => {}); }, [searchDate]);
  const loadMatches = useCallback(() => { if (user) api.get("/transport/matches").then(({ data }) => setMatches(data.suggestions)).catch(() => {}); }, [user]);
  const loadMissions = useCallback(() => { if (user) api.get("/transport/missions/mine").then(({ data }) => setMissions(data.missions)).catch(() => {}); }, [user]);
  const loadAlerts = useCallback(() => { if (user) api.get("/transport/alerts").then(({ data }) => setAlerts(data)).catch(() => {}); }, [user]);

  useEffect(() => { loadMeta(); loadOffers(); }, [loadMeta, loadOffers]);
  useEffect(() => { loadMatches(); loadMissions(); loadAlerts(); }, [loadMatches, loadMissions, loadAlerts]);
  useEffect(() => { if (!user) return; const t = setInterval(loadAlerts, 20000); return () => clearInterval(t); }, [user, loadAlerts]);

  const openAlerts = async () => {
    setShowAlerts((s) => !s);
    if (!showAlerts && alerts.unread > 0) { try { await api.post("/transport/alerts/read"); setAlerts((a) => ({ ...a, unread: 0 })); } catch {} }
  };

  const refreshAll = () => { loadMeta(); loadOffers(); loadMatches(); loadMissions(); loadAlerts(); };

  const book = async (ride, seats, done) => {
    try {
      const { data } = await api.post("/transport/missions", { ride_id: ride.id, seats });
      if (setUser) setUser((u) => u ? { ...u, credits: data.credits_remaining } : u);
      toast.success("Seat booked! Credits held safely until the mission completes. Awaiting driver confirmation.");
      setTab("missions"); refreshAll();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { done?.(); }
  };

  const missionAction = async (m, action) => {
    try {
      const { data } = await api.post(`/transport/missions/${m.id}/${action}`);
      const label = { confirm: "Confirmed ✓", start: "Mission started 🧭", complete: "Mission complete 🎉", cancel: `Cancelled${data?.refunded ? ` · ${data.refunded} credits refunded` : ""}` }[action];
      toast.success(label);
      if (action === "complete" || action === "cancel") refreshUser?.();
      refreshAll();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const runVerify = async () => {
    if (!verifyFile) { toast.error("Upload a clear photo of your government ID first."); return; }
    setVerifying(true);
    try {
      const { data } = await api.post("/transport/verify-id", { file_url: verifyFile.absoluteUrl });
      if (setUser) setUser((u) => u ? { ...u, credits: data.credits_remaining } : u);
      if (data.verified) { toast.success("Identity verified ✓ — you can now offer rides!"); loadMeta(); }
      else toast.error(data.reason || "We couldn't verify that document. Try a clearer, well-lit photo.");
      setVerifyFile(null);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setVerifying(false); }
  };

  if (!user) return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-28 pb-24 text-center">
      <Car size={40} className="text-brand mx-auto" />
      <h1 className="font-serif text-4xl mt-4">My Kind Global Community Travel</h1>
      <p className="text-sec mt-3" data-testid="transport-signin">Sign in to share a ride with fellow travellers — settled entirely in My Kind credits, kind and community-first.</p>
      <a href="/auth" className="btn-primary rounded-full h-11 px-6 text-sm inline-flex items-center gap-2 mt-5"><ArrowRight size={16} /> Sign in to start</a>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-28 pb-24">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><Car size={14} /> Global Community Travel</span>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter mt-3">Share the journey.</h1>
          <p className="text-sec mt-3 text-sm">A community carpool where everyday citizens — not taxis — share rides they're already taking. Post a route or find one nearby, settle in My Kind credits, and travel kinder & cheaper. My Kind keeps just {meta?.fee_pct || 9}%; the driver keeps {meta?.driver_pct || 91}%.</p>
        </div>
        <button onClick={openAlerts} data-testid="transport-alerts-btn" className="relative rud-card rounded-full w-11 h-11 grid place-items-center shrink-0">
          <Bell size={18} className="text-brand" />
          {alerts.unread > 0 && <span className="absolute -top-1 -right-1 bg-brand text-black text-[10px] rounded-full w-5 h-5 grid place-items-center font-bold" data-testid="alerts-unread">{alerts.unread}</span>}
        </button>
      </div>

      {showAlerts && (
        <div className="rud-card rounded-2xl p-4 mt-4" data-testid="alerts-feed">
          <div className="text-[11px] uppercase tracking-widest text-brand flex items-center gap-1.5 mb-2"><Sparkles size={13} /> My Kind Dispatcher</div>
          {alerts.alerts.length === 0 ? <div className="text-sec text-sm">No alerts yet. Post a route and matches will land here.</div> : (
            <div className="space-y-2 max-h-72 overflow-auto">
              {alerts.alerts.map((a) => (
                <div key={a.id} className="flex items-start gap-2 text-sm border-b border-rud pb-2" data-testid={`alert-${a.id}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-brand mt-1.5 shrink-0" />
                  <div><div>{a.text}</div><div className="text-[10px] text-sec">{new Date(a.created_at).toLocaleString()}</div></div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* verification status */}
      <div className="rud-card rounded-2xl p-4 mt-4 flex items-center gap-3 flex-wrap" data-testid="verify-status">
        <ShieldCheck size={18} className={verified ? "text-brand" : "text-sec"} />
        {verified ? (
          <span className="text-sm">Your identity is <b className="text-brand">verified</b> ✓ — reputation {meta?.me?.rep?.score ? `${meta.me.rep.score}★` : "New"}.</span>
        ) : (
          <>
            <span className="text-sm flex-1 min-w-[200px]">Verify your identity with My Kind to build trust and offer rides. A clear photo of your government ID ({meta?.fee_pct ? "" : ""}3 credits, privacy-safe — no ID numbers stored).</span>
            <div className="flex items-center gap-2">
              <FileUpload category="transport_id" accept="image/*" label={verifyFile ? "Change photo" : "Upload ID photo"} testId="verify-upload" onUploaded={setVerifyFile} />
              <button data-testid="verify-run" onClick={runVerify} disabled={verifying || !verifyFile} className="btn-primary rounded-full h-9 px-4 text-sm flex items-center gap-1.5 disabled:opacity-50">
                {verifying ? <Loader2 size={14} className="animate-spin" /> : <BadgeCheck size={14} />} Verify
              </button>
            </div>
          </>
        )}
      </div>

      {/* tabs */}
      <div className="flex flex-wrap gap-2 mt-6" data-testid="transport-tabs">
        {TABS.map((tb) => (
          <button key={tb.id} data-testid={`tab-${tb.id}`} onClick={() => { setTab(tb.id); if (tb.id === "matches") loadMatches(); if (tb.id === "missions") loadMissions(); if (tb.id === "find") loadOffers(); }}
            className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors flex items-center gap-1.5 ${tab === tb.id ? "bg-brand text-black border-brand" : "bg-elevated/50 text-sec border-rud hover:text-[var(--rud-text)]"}`}>
            <tb.icon size={14} /> {tb.label}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {tab === "find" && (
          <div className="space-y-5" data-testid="find-panel">
            <RideForm role="rider" meta={meta} country={country} onPosted={() => { refreshAll(); setTab("matches"); }} />
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-xl">Rides on offer</h3>
              <div className="flex items-center gap-2">
                <input type="date" data-testid="search-date" value={searchDate} onChange={(e) => setSearchDate(e.target.value)} className="rud-input rounded-lg px-3 py-1.5 text-sm" />
                <button onClick={loadOffers} data-testid="search-refresh" className="btn-ghost rounded-full w-9 h-9 grid place-items-center"><RefreshCw size={14} /></button>
              </div>
            </div>
            {offers.length === 0 ? <div className="rud-card rounded-xl p-6 text-center text-sec text-sm">No open ride offers yet. Post your request above and drivers on your path get alerted instantly.</div> : (
              <div className="space-y-3">{offers.map((r) => <OfferCard key={r.id} ride={r} onBook={book} canBook />)}</div>
            )}
          </div>
        )}

        {tab === "offer" && (
          <div className="space-y-4" data-testid="offer-panel">
            <RideForm role="driver" meta={meta} country={country} verified={verified} onPosted={() => { refreshAll(); setTab("matches"); }} />
            <p className="text-xs text-sec">You're a fellow traveller sharing your own trip — not a taxi. Offer only rides you're genuinely making.</p>
          </div>
        )}

        {tab === "tours" && <ToursPanel />}

        {tab === "stays" && <StaysPanel />}

        {tab === "matches" && (
          <div className="space-y-5" data-testid="matches-panel">
            {matches.length === 0 ? (
              <div className="rud-card rounded-xl p-6 text-center text-sec text-sm">No matches yet. Post a route in <b>Find</b> or <b>Offer</b> and My Kind auto-matches travellers on your path.</div>
            ) : matches.map((grp) => (
              <div key={grp.my_ride.id} data-testid={`match-group-${grp.my_ride.id}`}>
                <div className="text-[11px] uppercase tracking-widest text-brand mb-2 flex items-center gap-1.5">
                  <Sparkles size={13} /> Matches for your {grp.my_ride.role === "driver" ? "offer" : "request"}: {grp.my_ride.origin.label.split(",")[0]} → {grp.my_ride.destination.label.split(",")[0]} · {grp.my_ride.date}
                </div>
                <div className="space-y-3">
                  {grp.matches.map((r) => <OfferCard key={r.id} ride={r} onBook={book} canBook={grp.my_ride.role === "rider" && r.role === "driver"} />)}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "missions" && (
          <div className="space-y-3" data-testid="missions-panel">
            {missions.length === 0 ? <div className="rud-card rounded-xl p-6 text-center text-sec text-sm">No missions yet. Book a seat or confirm a rider to start your first shared journey.</div> : (
              missions.map((m) => <MissionCard key={m.id} m={m} onAction={missionAction} onChat={setChatMission} onReview={setReviewMission} />)
            )}
          </div>
        )}
      </div>

      {/* Why share the journey — community benefits (icon beside title) */}
      <div className="rud-card rounded-2xl p-6 mt-6 relative overflow-hidden" data-testid="transport-benefits">
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-brand/10 blur-2xl" />
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><UsersRound size={15} /> We're a community, not a cab company</div>
        <p className="mt-2 text-lg font-serif italic text-[var(--rud-text)]" data-testid="benefits-vasudhaiva">
          <span className="bg-brand/15 px-2 py-0.5 rounded">Vasudhaiva Kutumbakam</span> — the world is one family. We move together.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
          {[
            { icon: HandHeart, t: "One family, moving together", d: "You're not booking a stranger — you're sharing a journey with a fellow member of our community." },
            { icon: Coins, t: "Kinder on your wallet", d: "Pure cost-share — often ~70% below a taxi, settled in credits. Zero cash, zero haggling." },
            { icon: Leaf, t: "Kinder on the planet", d: "One shared car means fewer vehicles, less fuel and far less CO₂ for every seat filled." },
            { icon: ShieldCheck, t: "Trust, built in", d: "Every member is identity-verified, and mutual star reviews keep our community safe & kind." },
            { icon: Users, t: "Real people, real trips", d: "Everyday citizens sharing rides they're already taking — our community, never a taxi fleet." },
            { icon: Globe, t: "We grow together", d: "Drivers offset their trip, riders save, and referrers earn transit royalties. Everyone rises." },
          ].map((b, i) => (
            <div key={i} data-testid={`benefit-${i}`} className="flex items-start gap-3 rounded-xl border border-rud bg-elevated/40 p-4">
              <div className="w-9 h-9 rounded-lg bg-brand/15 grid place-items-center text-brand shrink-0"><b.icon size={17} /></div>
              <div className="min-w-0">
                <div className="font-medium text-sm">{b.t}</div>
                <div className="text-xs text-sec mt-0.5 leading-relaxed">{b.d}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {chatMission && <ChatModal mission={chatMission} onClose={() => setChatMission(null)} />}
      {reviewMission && <ReviewModal mission={reviewMission} onClose={() => setReviewMission(null)} onDone={() => { loadMissions(); loadMeta(); }} />}
    </div>
  );
}
